import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { api } from '../../lib/api';
import CohortInviteLinkPanel from './CohortInviteLinkPanel';

const CohortsManager = ({ token, userAccounts = [] }) => {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteLinkCohort, setInviteLinkCohort] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [formCohort, setFormCohort] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [membersTarget, setMembersTarget] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');
  const [addMemberId, setAddMemberId] = useState('');
  const [addMemberSaving, setAddMemberSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const result = await api.listCohorts(token);
      setCohorts(Array.isArray(result?.cohorts) ? result.cohorts : []);
      setError('');
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Failed to load cohorts.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const studentOptions = useMemo(() => (
    userAccounts
      .filter((u) => u && u.role === 'student' && u.accountStatus !== 'access_removed')
      .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''))
  ), [userAccounts]);

  const eligibleToAdd = useMemo(() => {
    const memberIds = new Set(members.map((m) => m.id));
    return studentOptions.filter((u) => !memberIds.has(u.id));
  }, [studentOptions, members]);

  const openCreate = () => {
    setFormCohort(null);
    setFormName('');
    setFormDescription('');
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (cohort) => {
    setFormCohort(cohort);
    setFormName(cohort.name);
    setFormDescription(cohort.description || '');
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
  };

  const submitForm = async (event) => {
    event.preventDefault();
    const name = formName.trim();
    if (!name) {
      setFormError('Name is required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      if (formCohort) {
        await api.updateCohort(formCohort.id, { name, description: formDescription }, token);
      } else {
        await api.createCohort({ name, description: formDescription }, token);
      }
      setShowForm(false);
      await refresh();
    } catch (err) {
      setFormError(err?.payload?.error || err?.message || 'Failed to save cohort.');
    } finally {
      setSaving(false);
    }
  };

  const removeCohort = async (cohort) => {
    if (!window.confirm(`Delete cohort "${cohort.name}"? Cases scoped to this cohort will fall back to tenant-wide visibility if no other cohorts remain on them.`)) {
      return;
    }
    try {
      await api.deleteCohort(cohort.id, token);
      await refresh();
    } catch (err) {
      window.alert(err?.payload?.error || err?.message || 'Failed to delete cohort.');
    }
  };

  const openMembers = async (cohort) => {
    setMembersTarget(cohort);
    setMembers([]);
    setMembersError('');
    setAddMemberId('');
    setMembersLoading(true);
    try {
      const result = await api.listCohortMembers(cohort.id, token);
      setMembers(Array.isArray(result?.members) ? result.members : []);
    } catch (err) {
      setMembersError(err?.payload?.error || err?.message || 'Failed to load members.');
    } finally {
      setMembersLoading(false);
    }
  };

  const closeMembers = () => {
    if (addMemberSaving) return;
    setMembersTarget(null);
  };

  const addMember = async () => {
    if (!membersTarget || !addMemberId) return;
    setAddMemberSaving(true);
    setMembersError('');
    try {
      const result = await api.addCohortMember(membersTarget.id, { userId: addMemberId }, token);
      if (result?.member) {
        setMembers((prev) => [...prev, result.member]
          .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '')));
      }
      setAddMemberId('');
      setCohorts((prev) => prev.map((c) => (c.id === membersTarget.id ? { ...c, memberCount: (c.memberCount || 0) + 1 } : c)));
    } catch (err) {
      setMembersError(err?.payload?.error || err?.message || 'Failed to add member.');
    } finally {
      setAddMemberSaving(false);
    }
  };

  const removeMember = async (member) => {
    if (!membersTarget) return;
    if (!window.confirm(`Remove ${member.displayName || member.email} from "${membersTarget.name}"?`)) return;
    try {
      await api.removeCohortMember(membersTarget.id, member.id, token);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      setCohorts((prev) => prev.map((c) => (c.id === membersTarget.id ? { ...c, memberCount: Math.max(0, (c.memberCount || 0) - 1) } : c)));
    } catch (err) {
      setMembersError(err?.payload?.error || err?.message || 'Failed to remove member.');
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-1">Cohorts</h4>
          <p className="text-muted mb-0">
            Group students into cohorts (e.g. <em>Year 3 MPharm 2026</em>) and target individual case studies at those groups. Cases with no cohort assigned remain visible to all students.
          </p>
        </div>
        <Button variant="primary" onClick={openCreate}>Add cohort</Button>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {loading ? (
        <div className="d-flex justify-content-center my-4"><Spinner animation="border" /></div>
      ) : cohorts.length === 0 ? (
        <Alert variant="secondary" className="mb-0">No cohorts yet. Create one to start scoping case studies.</Alert>
      ) : (
        <Table bordered responsive className="facilitator-admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Members</th>
              <th className="facilitator-admin-table__action-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cohorts.map((cohort) => (
              <React.Fragment key={cohort.id}>
                <tr>
                  <td className="fw-semibold">{cohort.name}</td>
                  <td>{cohort.description || <span className="text-muted">—</span>}</td>
                  <td><Badge bg="info">{cohort.memberCount || 0}</Badge></td>
                  <td>
                    <div className="d-flex flex-wrap gap-2">
                      <Button size="sm" variant="outline-primary" onClick={() => openMembers(cohort)}>Members</Button>
                      <Button
                        size="sm"
                        variant={inviteLinkCohort?.id === cohort.id ? 'primary' : 'outline-success'}
                        onClick={() => setInviteLinkCohort(inviteLinkCohort?.id === cohort.id ? null : cohort)}
                      >
                        Invite links
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => openEdit(cohort)}>Edit</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => removeCohort(cohort)}>Delete</Button>
                    </div>
                  </td>
                </tr>
                {inviteLinkCohort?.id === cohort.id ? (
                  <tr>
                    <td colSpan={4} className="p-0 border-top-0">
                      <CohortInviteLinkPanel cohort={cohort} token={token} />
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showForm} onHide={closeForm} centered>
        <Form onSubmit={submitForm}>
          <Modal.Header closeButton>
            <Modal.Title>{formCohort ? 'Edit cohort' : 'New cohort'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError ? <Alert variant="danger">{formError}</Alert> : null}
            <Form.Group className="mb-3" controlId="cohortName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formName}
                onChange={(event) => setFormName(event.target.value)}
                placeholder="e.g. Year 3 MPharm 2026"
                autoFocus
              />
            </Form.Group>
            <Form.Group controlId="cohortDescription">
              <Form.Label>Description <span className="text-muted small">(optional)</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formDescription}
                onChange={(event) => setFormDescription(event.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeForm} disabled={saving}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving…' : (formCohort ? 'Save changes' : 'Create cohort')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(membersTarget)} onHide={closeMembers} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{membersTarget ? `Members – ${membersTarget.name}` : 'Members'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {membersError ? <Alert variant="danger">{membersError}</Alert> : null}

          <div className="d-flex gap-2 align-items-end mb-3">
            <Form.Group className="flex-grow-1" controlId="addCohortMember">
              <Form.Label>Add student</Form.Label>
              <Form.Select
                value={addMemberId}
                onChange={(event) => setAddMemberId(event.target.value)}
                disabled={addMemberSaving || eligibleToAdd.length === 0}
              >
                <option value="">{eligibleToAdd.length === 0 ? 'All students already in this cohort' : 'Select a student…'}</option>
                {eligibleToAdd.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName || user.email} {user.displayName ? `(${user.email})` : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Button variant="primary" disabled={!addMemberId || addMemberSaving} onClick={addMember}>
              {addMemberSaving ? 'Adding…' : 'Add'}
            </Button>
          </div>

          {membersLoading ? (
            <div className="d-flex justify-content-center my-4"><Spinner animation="border" /></div>
          ) : members.length === 0 ? (
            <Alert variant="secondary" className="mb-0">No members yet.</Alert>
          ) : (
            <Table bordered responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Email</th>
                  <th className="facilitator-admin-table__action-col">Remove</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id}>
                    <td className="fw-semibold">{member.displayName || '—'}</td>
                    <td>{member.email}</td>
                    <td>
                      <Button size="sm" variant="outline-danger" onClick={() => removeMember(member)}>
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeMembers}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CohortsManager;
